import React, { useMemo, useState } from 'react';
import {
  App as AntdApp,
  Alert,
  Button,
  Card,
  Drawer,
  Input,
  List,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  FormOutlined,
  RobotOutlined,
  SendOutlined,
} from '@ant-design/icons';
import {
  buildEffectiveRegistry,
  getRegistryVersion,
  subscribeRegistryChanges,
} from '../config/covariateRegistry';
import { getDrugLabel } from '../constants/drugs';
import { aiAPI, getStoredToken, siteAPI } from '../services/api';
import { useAgent } from '../contexts/AgentContext';
import { useResponsive } from '../contexts/ResponsiveContext';
import { getCanonicalModelDisplayName } from '../utils/modelDisplay';
import VoiceInputButton from './VoiceInputButton';
import {
  getPopulationGroupLabel,
  stripConflictingNeonatalCovariates,
  type PopulationGroup,
} from '../constants/populationGroups';
import type {
  AIExtractionSnapshot,
  AIAssistantTurnResponse,
  AIAssistantStreamStatusPayload,
  AgentRequestedAction,
  ReadinessStatus,
  AgentFieldSource,
  IntakeCovariateItem,
} from '../types';
import { safeErrorMessage } from '../utils/errorHandler';
import { toAgentFieldKey, toFormFieldKey } from '../utils/fieldKeys';
import { convertUnitValue, unitFromResult } from '../utils/quantityUnits';

const { Text, Title } = Typography;
const { TextArea } = Input;

const AGENT_STYLES = `
  .agent-drawer .ant-drawer-content-wrapper {
    width: min(392px, calc(100vw - 280px)) !important;
    max-width: calc(100vw - 280px);
    max-height: 100dvh;
  }

  .agent-drawer .ant-drawer-content,
  .agent-drawer .ant-drawer-wrapper-body,
  .agent-drawer .ant-drawer-body {
    height: 100%;
    overflow: hidden;
  }

  .agent-workspace-fab {
    position: fixed;
    right: 28px;
    bottom: 28px;
    z-index: 1200;
  }

  .agent-workspace-fab .ant-btn {
    width: 58px;
    height: 58px;
    border-radius: 18px;
    border: none;
    background: linear-gradient(135deg, #001529 0%, #0A2744 100%);
    box-shadow: 0 16px 36px rgba(10, 25, 47, 0.26);
  }

  .agent-workspace-shell {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    min-width: 0;
    background: #FFFFFF;
    overflow: hidden;
  }

  .agent-docked {
    display: flex;
    flex-direction: column;
    flex: 0 0 392px;
    width: min(392px, calc(100vw - 320px));
    min-width: 344px;
    max-width: 392px;
    height: calc(100vh - 112px);
    min-height: calc(100vh - 112px);
    max-height: calc(100vh - 112px);
    position: sticky;
    top: 0;
    align-self: flex-start;
    overflow: hidden;
    border: 1px solid #E7EDF4;
    border-radius: 22px;
    background: #FFFFFF;
    box-shadow: 0 18px 36px rgba(10, 25, 47, 0.08);
  }

  .agent-workspace-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 16px;
    border-bottom: 1px solid #E7EDF4;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, #F7FAFD 100%);
    backdrop-filter: blur(10px);
  }

  .agent-workspace-header__meta {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    flex: 1;
  }

  .agent-workspace-header__close.ant-btn {
    border-radius: 12px;
    border: 1px solid #E7EDF4;
    background: rgba(255, 255, 255, 0.96);
    box-shadow: none;
    flex-shrink: 0;
  }

  .agent-workspace-body {
    flex: 1;
    display: flex;
    min-height: 0;
    min-width: 0;
    overflow: hidden;
    padding: 10px 12px 14px;
  }

  .agent-workspace-panel {
    display: flex;
    flex: 1;
    height: 100%;
    min-height: 0;
    min-width: 0;
    border-radius: 18px;
    border: 1px solid #E7EDF4;
    background: linear-gradient(180deg, #FFFFFF 0%, #FCFDFE 100%);
    box-shadow: 0 10px 30px rgba(10, 25, 47, 0.06);
    overflow: hidden;
  }

  .agent-workspace-panel.ant-card .ant-card-body {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 10px;
    min-height: 0;
    min-width: 0;
    overflow: hidden;
    padding: 12px;
  }

  .agent-workspace-results {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-height: 0;
    min-width: 0;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    padding-right: 2px;
  }

  .agent-workspace-results > .ant-space {
    display: flex !important;
    flex-direction: column;
    width: 100%;
    min-width: 0;
  }

  .agent-empty-state {
    flex: 1;
    min-height: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px 4px 6px;
  }

  .agent-empty-state__card {
    width: 100%;
    max-width: 100%;
    border-radius: 18px;
    border: 1px dashed #D9E3EE;
    background:
      radial-gradient(circle at top left, rgba(59, 130, 246, 0.08) 0%, transparent 36%),
      linear-gradient(180deg, #FBFDFF 0%, #FFFFFF 100%);
    padding: 16px 14px;
    text-align: center;
  }

  .agent-empty-state__icon {
    width: 42px;
    height: 42px;
    margin: 0 auto 10px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #1677FF;
    background: rgba(22, 119, 255, 0.1);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.85);
    font-size: 18px;
  }

  .agent-empty-state__title {
    color: #0F172A;
    font-size: 14px;
    font-weight: 700;
  }

  .agent-empty-state__text {
    margin-top: 6px;
    color: #667085;
    font-size: 12px;
    line-height: 1.6;
  }

  .agent-empty-state__tips {
    margin-top: 12px;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 6px;
  }

  .agent-empty-state__tip {
    border-radius: 999px;
    border: 1px solid #E6EDF5;
    background: rgba(255, 255, 255, 0.88);
    color: #475467;
    font-size: 11px;
    line-height: 1;
    padding: 7px 9px;
  }

  .agent-summary-card {
    border-radius: 16px;
    border: 1px solid #E7EDF4;
    background: linear-gradient(135deg, #FBFDFF 0%, #FFFFFF 100%);
    padding: 12px;
  }

  .agent-summary-card--warning {
    border-color: rgba(245, 158, 11, 0.28);
    background: linear-gradient(135deg, #FFF8EB 0%, #FFFFFF 100%);
  }

  .agent-summary-card--success {
    border-color: rgba(34, 197, 94, 0.24);
    background: linear-gradient(135deg, #F2FBF4 0%, #FFFFFF 100%);
  }

  .agent-summary-card__title {
    color: #0F172A;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.01em;
  }

  .agent-summary-card__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    min-width: 0;
  }

  .agent-summary-card__head-main {
    min-width: 0;
    flex: 1;
  }

  .agent-summary-card__eyebrow {
    color: #98A2B3;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .agent-summary-card__status.ant-tag {
    margin-inline-end: 0;
    border-radius: 999px;
    padding-inline: 8px;
    height: 22px;
    line-height: 20px;
    font-size: 10px;
    font-weight: 600;
    flex-shrink: 0;
  }

  .agent-summary-card__desc {
    margin-top: 4px;
    color: #667085;
    font-size: 12px;
    line-height: 1.6;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .agent-summary-card__banner {
    margin-top: 8px;
    border-radius: 12px;
    padding: 8px 10px;
    background: rgba(255, 255, 255, 0.8);
    border: 1px solid #EEF2F7;
    overflow: hidden;
  }

  .agent-summary-card__banner-title {
    color: #0F172A;
    font-size: 13px;
    font-weight: 700;
  }

  .agent-summary-card__banner-text {
    margin-top: 4px;
    color: #667085;
    font-size: 12px;
    line-height: 1.5;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .agent-overview-inline {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .agent-overview-chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    border-radius: 999px;
    border: 1px solid #EAF0F6;
    background: rgba(248, 250, 252, 0.88);
    padding: 5px 9px;
  }

  .agent-overview-chip--metric {
    min-height: 54px;
    border-color: #E5ECF4;
    background: linear-gradient(180deg, #FFFFFF 0%, #F8FBFF 100%);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9);
  }

  .agent-overview-chip__value {
    color: #0F172A;
    font-size: 14px;
    font-weight: 800;
    line-height: 1;
  }

  .agent-overview-chip__label {
    color: #667085;
    font-size: 11px;
  }

  .agent-section-card.ant-card {
    border-radius: 14px;
    border: 1px solid #EEF2F7;
    box-shadow: none;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.92);
  }

  .agent-section-card.ant-card .ant-card-head {
    min-height: 38px;
    padding: 0 12px;
    border-bottom: none;
  }

  .agent-section-card.ant-card .ant-card-head-title {
    padding: 10px 0 0;
    font-size: 12px;
    color: #475467;
    font-weight: 600;
  }

  .agent-section-card.ant-card .ant-card-body {
    padding: 0 12px 12px;
  }

  .agent-section-card .ant-list .ant-list-item {
    padding: 6px 0;
    border-block-end: none;
  }

  .agent-result-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .agent-pill.ant-tag {
    margin-inline-end: 0;
    border-radius: 999px;
    padding-inline: 7px;
    font-size: 10px;
    line-height: 18px;
    height: 20px;
    border-color: rgba(148, 163, 184, 0.24);
  }

  .agent-quick-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .agent-quick-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    min-width: 0;
    border-radius: 12px;
    border: 1px solid #EEF2F7;
    background: #FFFFFF;
    padding: 8px 10px;
  }

  .agent-quick-row__label {
    color: #475467;
    font-size: 12px;
    font-weight: 600;
  }

  .agent-quick-row__value {
    margin-top: 4px;
    color: #0F172A;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.5;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .agent-field-section {
    border: 1px solid #EEF2F7;
    border-radius: 14px;
    background: linear-gradient(180deg, #FCFDFE 0%, #FFFFFF 100%);
    padding: 10px;
  }

  .agent-field-section__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 8px;
  }

  .agent-field-section__subtext {
    color: #667085;
    font-size: 11px;
    margin-top: 2px;
  }

  .agent-field-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .agent-field-row {
    border: 1px solid #EEF2F7;
    border-radius: 12px;
    background: #FFFFFF;
    padding: 8px 10px;
    min-width: 0;
  }

  .agent-field-row__top {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(72px, 116px);
    gap: 10px;
    align-items: start;
    min-width: 0;
  }

  .agent-field-row__identity {
    min-width: 0;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
  }

  .agent-field-row__label {
    min-width: 0;
    color: #1F2937;
    font-size: 12px;
    font-weight: 600;
  }

  .agent-field-row__value {
    max-width: 116px;
    text-align: right;
    color: #0F172A;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.45;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .agent-field-row__sub {
    margin-top: 6px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .agent-field-row__evidence {
    flex: 1;
    min-width: 0;
    color: #667085;
    font-size: 11px;
    line-height: 1.45;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .agent-field-row__meta {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 6px;
  }

  .agent-workspace-input {
    margin-top: 2px;
    padding-top: 10px;
    border-top: 1px solid #EEF2F7;
    flex-shrink: 0;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.78) 0%, #FFFFFF 100%);
    backdrop-filter: blur(8px);
  }

  .agent-workspace-input .ant-input {
    border-radius: 14px;
    background: linear-gradient(180deg, #FCFDFE 0%, #FFFFFF 100%);
    font-size: 12px;
  }

  .agent-workspace-status-row {
    margin-top: 8px;
    display: flex;
    justify-content: flex-start;
  }

  .agent-workspace-actions {
    margin-top: 10px;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .agent-workspace-shortcuts,
  .agent-workspace-actions__main,
  .agent-workspace-actions__right {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .agent-workspace-shortcuts {
    width: 100%;
    flex-wrap: wrap;
    min-height: 30px;
    padding-bottom: 2px;
  }

  .agent-workspace-actions__main {
    width: 100%;
    justify-content: space-between;
    gap: 14px;
    padding-top: 2px;
  }

  .agent-workspace-actions__right {
    margin-left: auto;
    flex-wrap: nowrap;
    flex: 0 0 auto;
  }

  .agent-clear-action.ant-btn {
    height: 34px;
    border-radius: 12px;
  }

  .agent-primary-action.ant-btn {
    height: 34px;
    border-radius: 12px;
    box-shadow: 0 8px 18px rgba(22, 119, 255, 0.16);
  }

  @media (max-width: 1024px) {
    .agent-docked {
      display: none;
    }

    .agent-drawer .ant-drawer-content-wrapper {
      height: 100dvh !important;
      width: 100vw !important;
      max-width: 100vw;
      max-height: 100dvh;
    }

    .agent-workspace-fab {
      right: 20px;
      bottom: calc(92px + env(safe-area-inset-bottom));
    }

    .agent-workspace-body {
      padding: 8px 8px 10px;
    }

    .agent-workspace-header {
      padding: 10px 12px;
      gap: 8px;
    }

    .agent-workspace-panel {
      border-radius: 16px;
    }

    .agent-workspace-panel.ant-card .ant-card-body {
      gap: 8px;
      padding: 10px;
    }

    .agent-workspace-results {
      gap: 8px;
    }

    .agent-empty-state {
      padding: 4px 0 4px;
      align-items: flex-start;
      justify-content: flex-start;
    }

    .agent-empty-state__card {
      border-radius: 16px;
      padding: 14px 12px;
      text-align: left;
    }

    .agent-empty-state__icon {
      margin: 0 0 10px;
      width: 38px;
      height: 38px;
      border-radius: 12px;
      font-size: 16px;
    }

    .agent-empty-state__title {
      font-size: 13px;
    }

    .agent-empty-state__text {
      margin-top: 4px;
      font-size: 11.5px;
      line-height: 1.55;
    }

    .agent-empty-state__tips {
      margin-top: 10px;
      justify-content: flex-start;
      gap: 5px;
    }

    .agent-empty-state__tip {
      font-size: 10px;
      padding: 6px 8px;
    }

    .agent-summary-card {
      border-radius: 14px;
      padding: 10px;
      background:
        linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, #FFFFFF 44%, #F9FBFE 100%),
        linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, transparent 52%);
    }

    .agent-summary-card__title {
      font-size: 13px;
    }

    .agent-summary-card__head {
      flex-direction: column;
      align-items: stretch;
      gap: 8px;
    }

    .agent-summary-card__status.ant-tag {
      align-self: flex-start;
    }

    .agent-summary-card__eyebrow {
      font-size: 9px;
      letter-spacing: 0.1em;
    }

    .agent-summary-card__status.ant-tag {
      padding-inline: 7px;
      height: 20px;
      line-height: 18px;
      font-size: 9px;
    }

    .agent-summary-card__desc {
      margin-top: 3px;
      font-size: 11.5px;
      line-height: 1.52;
    }

    .agent-summary-card__banner {
      margin-top: 7px;
      border-radius: 11px;
      padding: 7px 9px;
    }

    .agent-summary-card__banner-title {
      font-size: 12px;
    }

    .agent-summary-card__banner-text {
      margin-top: 3px;
      font-size: 11.5px;
      line-height: 1.48;
    }

    .agent-overview-inline {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 6px;
    }

    .agent-overview-chip {
      width: 100%;
      min-height: 0;
      gap: 3px;
      padding: 7px 8px;
      border-radius: 12px;
      flex-direction: column;
      align-items: flex-start;
    }

    .agent-overview-chip__value {
      font-size: 13px;
      line-height: 1.05;
    }

    .agent-overview-chip__label {
      font-size: 10px;
      line-height: 1.2;
    }

    .agent-section-card.ant-card {
      border-radius: 12px;
    }

    .agent-section-card.ant-card .ant-card-head {
      min-height: 34px;
      padding: 0 10px;
    }

    .agent-section-card.ant-card .ant-card-head-title {
      padding: 8px 0 0;
      font-size: 11px;
    }

    .agent-section-card.ant-card .ant-card-body {
      padding: 0 10px 10px;
    }

    .agent-section-card .ant-list .ant-list-item {
      padding: 5px 0;
    }

    .agent-quick-list {
      gap: 6px;
    }

    .agent-quick-row {
      gap: 6px;
      border-radius: 11px;
      padding: 7px 9px;
      flex-direction: column;
      align-items: flex-start;
    }

    .agent-quick-row__label {
      font-size: 11px;
    }

    .agent-quick-row__value {
      margin-top: 2px;
      font-size: 12px;
      line-height: 1.4;
    }

    .agent-field-section {
      border-radius: 12px;
      padding: 8px;
    }

    .agent-field-section__header {
      margin-bottom: 6px;
    }

    .agent-field-list {
      gap: 5px;
    }

    .agent-field-row {
      border-radius: 11px;
      padding: 7px 9px;
    }

    .agent-field-row__label,
    .agent-field-row__value {
      font-size: 11.5px;
    }

    .agent-field-row__sub {
      margin-top: 5px;
    }

    .agent-workspace-input {
      margin-top: 0;
      padding-top: 8px;
    }

    .agent-workspace-input .ant-input {
      border-radius: 12px;
      font-size: 11.5px;
    }

    .agent-workspace-status-row {
      margin-top: 7px;
    }

    .agent-workspace-header__close.ant-btn {
      min-width: 40px;
      height: 40px;
    }

    .agent-field-row__top {
      grid-template-columns: 1fr;
    }

    .agent-field-row__value {
      max-width: none;
      text-align: left;
    }

    .agent-field-row__sub {
      flex-direction: column;
      align-items: flex-start;
    }

    .agent-field-row__meta {
      justify-content: flex-start;
    }

    .agent-workspace-actions {
      gap: 10px;
    }

    .agent-workspace-shortcuts {
      gap: 6px;
      padding-bottom: 3px;
    }

    .agent-workspace-actions__main {
      gap: 10px;
      padding-top: 3px;
    }

    .agent-workspace-actions__right > *,
    .agent-workspace-actions__main > * {
      flex: 0 0 auto;
    }

    .agent-workspace-shortcuts .ant-btn {
      white-space: nowrap;
    }

    .agent-clear-action.ant-btn,
    .agent-primary-action.ant-btn {
      min-width: 104px;
      height: 32px;
      padding-inline: 12px;
      border-radius: 11px;
    }

    .agent-clear-action.ant-btn {
      min-width: 86px;
    }

    .agent-workspace-actions .ant-btn-icon-only {
      width: 32px;
      min-width: 32px;
      height: 32px;
      border-radius: 11px;
    }
  }
`;

const READINESS_META: Record<ReadinessStatus, { type: 'success' | 'warning' | 'info'; title: string }> = {
  match_ready: { type: 'success', title: '已满足匹配条件' },
  review_needed: { type: 'warning', title: '需要人工确认' },
  needs_info: { type: 'info', title: '仍需补充信息' },
};

const COMPUTE_META: Record<string, { color: string; label: string }> = {
  not_requested: { color: 'default', label: '未触发计算' },
  blocked: { color: 'orange', label: '计算受阻' },
  ready: { color: 'blue', label: '可进入计算' },
  completed: { color: 'green', label: '已生成建议' },
  failed: { color: 'red', label: '计算失败' },
};

const FIELD_LABELS: Record<string, string> = {
  age_pma: '校正胎龄',
  age: '年龄',
  age_pna: '日龄',
  age_years: '年龄',
  bmi: 'BMI',
  bsa: 'BSA',
  birth_date: '出生日期',
  crcl: 'CrCl',
  current_date: '当前日期',
  diagnosis: '诊断',
  drug_name: '药物',
  egfr: '估算肾小球滤过率',
  gender: '性别',
  gestational_age: '胎龄',
  height: '身高',
  patient_code: '住院号',
  scr: '血清肌酐',
  scr_unit: 'Scr 单位',
  weight: '体重',
};

const FIELD_STATUS_META: Record<string, { color: string; label: string }> = {
  confirmed: { color: 'green', label: '已确认' },
  ai_suggested: { color: 'blue', label: 'AI 建议' },
  needs_input: { color: 'default', label: '待补充' },
  conflict_pending: { color: 'orange', label: '待确认' },
};

const CONFIDENCE_META: Record<string, { color: string; label: string }> = {
  computed: { color: 'default', label: '计算' },
  high: { color: 'geekblue', label: '高' },
  medium: { color: 'gold', label: '中' },
  low: { color: 'volcano', label: '低' },
};

const formatFieldLabel = (
  key: string,
  registry?: Record<string, { label?: string } | undefined>,
) => {
  const canonicalKey = toAgentFieldKey(key);
  const configuredLabel = registry?.[canonicalKey]?.label;
  if (configuredLabel) return configuredLabel;
  return FIELD_LABELS[canonicalKey] || canonicalKey.replace(/_/g, ' ');
};
const formatFieldLabelWithKey = (
  key: string,
  registry?: Record<string, { label?: string } | undefined>,
) => {
  const label = formatFieldLabel(key, registry);
  if (label.toLowerCase() === key.toLowerCase()) return label;
  return `${label} (${key})`;
};

const formatReason = (reason: string) => {
  if (reason === 'verifier_mismatch') return 'AI 初提取与校验结果不一致';
  return reason.replace(/_/g, ' ');
};

const formatFollowUpQuestion = (question: string) => question.replace(
  /\b([a-z_][a-z0-9_]*)\b/gi,
  (matched) => {
    if (!matched.includes('_')) return matched;
    return formatFieldLabelWithKey(matched);
  },
);

const formatGender = (value: any) => {
  const normalized = String(value ?? '').toLowerCase();
  if (normalized === 'male') return '男';
  if (normalized === 'female') return '女';
  return String(value ?? '-');
};

const formatNumeric = (value: number) => {
  if (Number.isInteger(value)) return String(value);
  if (Math.abs(value) >= 10) return value.toFixed(1);
  if (Math.abs(value) >= 1) return value.toFixed(2);
  return value.toFixed(3);
};

const formatPatchValue = (patch: AIAssistantTurnResponse['field_patches'][number]) => {
  const value = patch.normalized_value;
  if (value === undefined || value === null || value === '') return '-';
  if (patch.key === 'drug_name') return getDrugLabel(String(value), String(value));
  if (patch.key === 'gender') return formatGender(value);
  if (typeof value === 'boolean') return value ? '是' : '否';
  if (typeof value === 'number') {
    const text = formatNumeric(value);
    return patch.unit ? `${text} ${patch.unit}` : text;
  }
  return String(value);
};

const normalizeCurrentFields = (values: Record<string, any>) => {
  const normalized = Object.entries(values || {}).reduce<Record<string, unknown>>(
    (accumulator, [key, value]) => {
      accumulator[toAgentFieldKey(key)] = value;
      return accumulator;
    },
    {},
  );
  return stripConflictingNeonatalCovariates(normalized) as Record<string, any>;
};

const buildCurrentFieldMeta = (
  currentFields: Record<string, any>,
  snapshot: AIExtractionSnapshot | null,
) => {
  const meta: Record<string, { source: AgentFieldSource }> = {};
  Object.entries(snapshot?.field_sources || {}).forEach(([key, source]) => {
    meta[key] = { source };
  });
  Object.entries(currentFields || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (!meta[key]) meta[key] = { source: 'manual' };
  });
  return meta;
};

const buildSnapshot = (result: AIAssistantTurnResponse, appliedKeys: string[] = []): AIExtractionSnapshot => ({
  detected_population_group: (result.population_group as PopulationGroup | undefined) || null,
  selected_population_group: (result.selected_population_group as PopulationGroup | undefined) || (result.population_group as PopulationGroup | undefined) || null,
  recommended_population_group: (result.recommended_population_group as PopulationGroup | undefined) || (result.population_group as PopulationGroup | undefined) || null,
  resolved_scope_group: result.resolved_scope_group || null,
  detected_drug_id: result.detected_drug_id || null,
  manual_population_override: null,
  candidate_scope_groups: result.candidate_scope_groups || [],
  confidence: result.confidence,
  decision_basis: result.decision_basis || [],
  required_covariate_keys: result.required_covariate_keys || [],
  optional_covariate_keys: result.optional_covariate_keys || [],
  collection_required_covariate_keys: result.collection_required_covariate_keys || result.required_covariate_keys || [],
  collection_optional_covariate_keys: result.collection_optional_covariate_keys || result.optional_covariate_keys || [],
  scope_locked: result.scope_locked || false,
  lock_respected: result.lock_respected || false,
  candidate_fields: result.candidate_fields || [],
  recommended_apply_keys: result.recommended_apply_keys || [],
  applied_keys: appliedKeys,
  assistant_reply: result.assistant_reply,
  field_patches: result.field_patches,
  missing_required: result.missing_required,
  conflicts: result.conflicts,
  follow_up_questions: result.follow_up_questions,
  readiness_status: result.readiness_status,
  workflow_stage: result.workflow_stage,
  compute_status: result.compute_status,
  matched_model: result.matched_model || null,
  dose_recommendation: result.dose_recommendation || null,
  field_sources: result.applied_field_sources || {},
  intent: result.intent,
  extract_version: result.extract_version,
  intake_context: result.intake_context || null,
  intake_context_error: result.intake_context_error || null,
  intake_context_error_code: result.intake_context_error_code || null,
});

const snapshotToResult = (snapshot: AIExtractionSnapshot | null): AIAssistantTurnResponse | null => {
  if (!snapshot?.readiness_status) return null;
  const snapshotDrugField = snapshot.candidate_fields.find((field) => field.key === 'drug_name');
  return {
    assistant_reply: snapshot.assistant_reply || '',
    population_group: snapshot.detected_population_group || null,
    selected_population_group: snapshot.selected_population_group || null,
    recommended_population_group: snapshot.recommended_population_group || null,
    resolved_scope_group: snapshot.resolved_scope_group || null,
    detected_drug_id: snapshot.detected_drug_id || (snapshotDrugField?.value ? String(snapshotDrugField.value) : null),
    field_patches: snapshot.field_patches || [],
    missing_required: snapshot.missing_required || [],
    conflicts: snapshot.conflicts || [],
    follow_up_questions: snapshot.follow_up_questions || [],
    readiness_status: snapshot.readiness_status,
    candidate_scope_groups: snapshot.candidate_scope_groups || [],
    confidence: snapshot.confidence,
    decision_basis: snapshot.decision_basis || [],
    required_covariate_keys: snapshot.required_covariate_keys || [],
    optional_covariate_keys: snapshot.optional_covariate_keys || [],
    collection_required_covariate_keys: snapshot.collection_required_covariate_keys || snapshot.required_covariate_keys || [],
    collection_optional_covariate_keys: snapshot.collection_optional_covariate_keys || snapshot.optional_covariate_keys || [],
    scope_locked: snapshot.scope_locked || false,
    lock_respected: snapshot.lock_respected || false,
    workflow_stage: snapshot.workflow_stage,
    compute_status: snapshot.compute_status,
    matched_model: snapshot.matched_model || null,
    dose_recommendation: snapshot.dose_recommendation || null,
    applied_field_sources: snapshot.field_sources || {},
    intent: snapshot.intent,
    candidate_fields: snapshot.candidate_fields || [],
    recommended_apply_keys: snapshot.recommended_apply_keys || [],
    extract_version: snapshot.extract_version,
    intake_context: snapshot.intake_context || null,
    intake_context_error: snapshot.intake_context_error || null,
    intake_context_error_code: snapshot.intake_context_error_code || null,
  };
};

const shouldShowComputeSection = (result: AIAssistantTurnResponse | null): boolean => {
  if (!result) return false;
  if (result.matched_model || result.dose_recommendation) return true;
  return ['blocked', 'completed', 'failed'].includes(String(result.compute_status || ''));
};

const buildDoseCandidates = (result: AIAssistantTurnResponse | null): any[] => {
  const candidates = result?.dose_recommendation?.candidates || [];
  if (candidates.length > 0) {
    return candidates;
  }

  const recommendation = result?.dose_recommendation?.recommendation;
  if (recommendation && Object.keys(recommendation).length > 0) {
    return [{
      ...recommendation,
      label: recommendation.label || '候选方案 1',
    }];
  }

  return [];
};

const hasMeaningfulWorkspaceValue = (value: any): boolean => {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

const AgentWorkspace: React.FC = () => {
  const {
    businessContext,
    boundForm,
    resetWorkspace,
    closeDrawer,
    pendingFill,
    setPendingFill,
    activePopulationGroup,
    setActivePopulationGroup,
    setPopulationSource,
    populationLocked,
    setPopulationLocked,
    aiExtractionSnapshot,
    setAIExtractionSnapshot,
  } = useAgent();
  const { isMobileShell } = useResponsive();
  const { message } = AntdApp.useApp();
  const [registryVersion, setRegistryVersion] = useState(() => getRegistryVersion());
  const [inputText, setInputText] = useState('');
  const [intakeTemplates, setIntakeTemplates] = useState<Array<{ title: string; content: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [streamStatus, setStreamStatus] = useState('');
  const [result, setResult] = useState<AIAssistantTurnResponse | null>(null);
  const latestFieldsRef = React.useRef<Record<string, any>>({});
  const lastAutoComputeKeyRef = React.useRef('');
  const effectiveRegistry = useMemo(() => buildEffectiveRegistry(), [registryVersion]);

  React.useEffect(() => subscribeRegistryChanges(setRegistryVersion), []);
  React.useEffect(() => {
    let cancelled = false;
    siteAPI.getAgentIntakeTemplates()
      .then((templates) => {
        if (!cancelled) setIntakeTemplates(templates);
      })
      .catch(() => {
        if (!cancelled) setIntakeTemplates([]);
      });
    return () => { cancelled = true; };
  }, []);

  const currentFormValues = useMemo(() => {
    try {
      return normalizeCurrentFields(boundForm?.getFieldsValue?.(true) || businessContext.patientContext || {});
    } catch {
      return normalizeCurrentFields(businessContext.patientContext || {});
    }
  }, [boundForm, businessContext.patientContext, pendingFill]);

  const snapshotResult = useMemo(() => snapshotToResult(aiExtractionSnapshot), [aiExtractionSnapshot]);
  const activeResult = result || snapshotResult;
  const conditionalRequirementItems = useMemo(
    () => activeResult?.intake_context?.conditional_required_covariates || [],
    [activeResult?.intake_context?.conditional_required_covariates],
  );
  const optionalRequirementItems = useMemo(() => {
    const byKey = new Map<string, IntakeCovariateItem>();
    [
      ...(activeResult?.intake_context?.suggested_covariates || []),
      ...(activeResult?.intake_context?.deferred_covariates || []),
    ].forEach((item) => {
      if (item.requirement_level === 'optional' || (!item.requirement_level && !item.required)) {
        byKey.set(item.key, item);
      }
    });
    return Array.from(byKey.values());
  }, [activeResult?.intake_context?.deferred_covariates, activeResult?.intake_context?.suggested_covariates]);
  const canClearWorkspace = useMemo(() => (
    Boolean(inputText.trim())
    || Boolean(activeResult)
    || Boolean(aiExtractionSnapshot)
    || Object.values(currentFormValues || {}).some((value) => hasMeaningfulWorkspaceValue(value))
  ), [activeResult, aiExtractionSnapshot, currentFormValues, inputText]);
  const readinessMeta = activeResult ? READINESS_META[activeResult.readiness_status] : null;
  const requestedAction: AgentRequestedAction = 'extract';
  const computeSectionVisible = shouldShowComputeSection(activeResult);
  const doseCandidates = useMemo(() => buildDoseCandidates(activeResult), [activeResult]);
  const doseMassUnit = unitFromResult(activeResult?.dose_recommendation, 'dose', 'mg');
  const formatCandidateDosePerKg = (value: unknown) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '-';
    const converted = convertUnitValue(numeric, 'mg/kg', `${doseMassUnit}/kg`, 'dose_per_weight');
    return `${formatNumeric(converted)} ${doseMassUnit}/kg`;
  };
  React.useEffect(() => {
    latestFieldsRef.current = currentFormValues;
  }, [currentFormValues]);

  const applyStreamResult = React.useCallback((response: AIAssistantTurnResponse) => {
    setResult(response);
    setAIExtractionSnapshot(buildSnapshot(response));
    if (response.population_group && !populationLocked) {
      setActivePopulationGroup(response.population_group);
      setPopulationSource('ai');
    }
  }, [
    populationLocked,
    setAIExtractionSnapshot,
    setActivePopulationGroup,
    setPopulationSource,
  ]);

  const handleStreamStatus = React.useCallback((payload: AIAssistantStreamStatusPayload) => {
    setStreamStatus(payload.message || '');
  }, []);

  const applyMatchedModel = React.useCallback((payload: { model_id: string; model_name?: string }) => {
    setResult((prev) => (prev ? { ...prev, matched_model: payload } : prev));
  }, []);

  const handleCandidateModelsUpdated = React.useCallback((payload: Record<string, any>) => {
    setStreamStatus(`候选模型已更新，当前 ${payload.candidate_models?.length || 0} 个`);
    setResult((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        intake_context: prev.intake_context ? {
          ...prev.intake_context,
          candidate_models: payload.candidate_models || prev.intake_context.candidate_models,
          recommended_models: payload.recommended_models || prev.intake_context.recommended_models,
          excluded_models: payload.excluded_models || prev.intake_context.excluded_models,
        } : prev.intake_context,
      };
    });
  }, []);

  const handleBlockingFieldsUpdated = React.useCallback((payload: Record<string, any>) => {
    const blockers = payload.blocking_covariates || [];
    setStreamStatus(blockers.length ? `仍有 ${blockers.length} 个阻断字段待补齐` : '阻断字段已清空，接近可确认状态');
  }, []);

  const handleCovariateRequirementsUpdated = React.useCallback((payload: Record<string, any>) => {
    if (payload.error) {
      setStreamStatus(String(payload.error));
      return;
    }
    const requiredCount = payload.required_now?.length || 0;
    const conditionalCount = payload.conditional_required?.length || 0;
    setStreamStatus(`必填 ${requiredCount} · 模型相关 ${conditionalCount}`);
  }, []);

  const buildAssistantPayload = React.useCallback((
    text: string,
    fields: Record<string, any>,
  ) => ({
    text,
    drug_name: fields.drug_name || businessContext.drugName,
    population_group: activePopulationGroup || undefined,
    current_fields: fields,
    current_field_meta: buildCurrentFieldMeta(fields, aiExtractionSnapshot),
    requested_action: requestedAction,
    case_context: {
      tdm_data: businessContext.tdmData,
      calc_type: businessContext.workflowStep || undefined,
      model_id: businessContext.modelId,
      calc_result: businessContext.calcResult,
    },
  }), [
    activePopulationGroup,
    aiExtractionSnapshot,
    businessContext.calcResult,
    businessContext.drugName,
    businessContext.modelId,
    businessContext.tdmData,
    businessContext.workflowStep,
    requestedAction,
  ]);

  const runAssistantTurn = async (text: string) => {
    if (!text.trim() || loading) return;
    if (!getStoredToken()) {
      message.warning('登录状态已失效，请重新登录后再运行 Agent');
      return;
    }
    setLoading(true);
    setStreamStatus('正在提取协变量并校验…');
    try {
      const response = await aiAPI.assistantTurnStream(
        buildAssistantPayload(text, currentFormValues),
        {
          onStatus: handleStreamStatus,
          onBaseResult: applyStreamResult,
          onMatchedModel: applyMatchedModel,
          onCandidateModelsUpdated: handleCandidateModelsUpdated,
          onBlockingFieldsUpdated: handleBlockingFieldsUpdated,
          onCovariateRequirementsUpdated: handleCovariateRequirementsUpdated,
          onReadyToConfirm: () => setStreamStatus('已达到最终模型确认条件'),
          onResult: applyStreamResult,
        },
      );
      setInputText('');
      applyStreamResult(response);
    } catch (err: any) {
      if (err?.response?.status === 401) {
        message.error('登录状态已过期，请重新登录后重试');
        return;
      }
      message.error(safeErrorMessage(err?.response?.data?.detail, 'AI 请求失败'));
    } finally {
      setStreamStatus('');
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (!activeResult) return;
    if (!boundForm) {
      message.warning('当前没有可回填的病例表单');
      return;
    }

    const applied: Record<string, any> = {};
    const appliedKeys: string[] = [];
    const fieldSources: Record<string, AgentFieldSource> = {};
    const retainedResultKeys = new Set<string>([
      ...activeResult.field_patches.map((patch) => patch.key),
      ...activeResult.conflicts.map((item) => item.key),
    ]);
    const orderedKeys = (() => {
      const preferredKeys = activeResult.recommended_apply_keys?.length
        ? activeResult.recommended_apply_keys
        : [];
      const combined: string[] = [];
      const seen = new Set<string>();
      [...preferredKeys, ...activeResult.field_patches.map((patch) => patch.key)].forEach((key) => {
        if (!key || seen.has(key)) return;
        seen.add(key);
        combined.push(key);
      });
      return combined;
    })();
    const patchMap = new Map(activeResult.field_patches.map((patch) => [patch.key, patch] as const));
    const expandedKeys: string[] = [];
    const seenKeys = new Set<string>();

    const addKeyWithDependencies = (key: string) => {
      if (seenKeys.has(key)) return;
      seenKeys.add(key);
      const patch = patchMap.get(key);
      const isComputed = String(patch?.confidence_label || '').toLowerCase() === 'computed';
      if (patch && isComputed) {
        const derivedFrom = patch.derived_from || effectiveRegistry[key]?.derived_from || [];
        derivedFrom.forEach((dependencyKey) => addKeyWithDependencies(dependencyKey));
      }
      expandedKeys.push(key);
    };

    orderedKeys.forEach((key) => addKeyWithDependencies(key));

    expandedKeys.forEach((key) => {
      const patch = patchMap.get(key);
      if (!patch || patch.field_status === 'conflict_pending') return;
      if (patch.apply_to_form === false) return;
      if (patch.normalized_value === undefined || patch.normalized_value === null) return;
      const formKey = toFormFieldKey(key);
      const registryDef = effectiveRegistry[key];
      applied[formKey] = registryDef?.type === 'number'
        ? Number(patch.normalized_value)
        : patch.normalized_value;
      appliedKeys.push(key);
      const isComputed = String(patch.confidence_label || '').toLowerCase() === 'computed';
      const backendSuggestedSource = activeResult.applied_field_sources?.[key];
      const shouldKeepDerivedSource = isComputed
        ? true
        : backendSuggestedSource === 'derived';
      fieldSources[key] = shouldKeepDerivedSource
        ? 'derived'
        : (backendSuggestedSource && backendSuggestedSource !== 'derived'
          ? backendSuggestedSource
          : 'ai_extracted');
    });

    if (activeResult.detected_drug_id && !applied.drug_name) {
      applied.drug_name = activeResult.detected_drug_id;
      appliedKeys.push('drug_name');
      fieldSources.drug_name = activeResult.applied_field_sources?.drug_name || 'ai_extracted';
      retainedResultKeys.add('drug_name');
    }

    const aiOwnedSources = new Set<AgentFieldSource>(['ai_extracted', 'ai_verified', 'derived']);
    const clearKeys = Object.entries(aiExtractionSnapshot?.field_sources || {}).reduce<string[]>((accumulator, [key, source]) => {
      if (key === 'drug_name') return accumulator;
      if (!aiOwnedSources.has(source)) return accumulator;
      if (retainedResultKeys.has(key)) return accumulator;
      if (!hasMeaningfulWorkspaceValue(currentFormValues[key])) return accumulator;
      accumulator.push(toFormFieldKey(key));
      return accumulator;
    }, []);

    setPendingFill({
      values: applied,
      fieldSources,
      clearKeys,
    });
    if (activeResult.population_group && !populationLocked) {
      setActivePopulationGroup(activeResult.population_group);
      setPopulationSource('ai');
    }
    setAIExtractionSnapshot(buildSnapshot(activeResult, Array.from(new Set(appliedKeys))));
    message.success(
      activeResult.readiness_status === 'match_ready'
        ? '已回填字段，当前病例已进入匹配准备状态'
        : '已回填字段，请继续补充或确认剩余信息',
    );
  };

  const handleClearWorkspace = () => {
    setInputText('');
    setStreamStatus('');
    setResult(null);
    setAIExtractionSnapshot(null);
    setPendingFill(null);
    setPopulationLocked(false);
    lastAutoComputeKeyRef.current = '';
    if (businessContext.workflowStep === 'patient' && resetWorkspace) {
      resetWorkspace();
      return;
    }
    if (boundForm) {
      boundForm.resetFields();
    }
    setActivePopulationGroup(null);
    setPopulationSource(null);
  };

  return (
    <div className="agent-workspace-shell">
      <div className="agent-workspace-header">
        <div className="agent-workspace-header__meta">
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 12,
              background: 'rgba(24, 144, 255, 0.12)',
              color: '#1677FF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 16,
            }}
          >
            <RobotOutlined />
          </div>
          <div>
            <Title level={5} style={{ margin: 0 }}>Agent 顾问</Title>
            <Text type="secondary" style={{ fontSize: 12 }}>
              用于病历粘贴、协变量抽取、缺失补问与结构化录入辅助
            </Text>
          </div>
        </div>
        <Button
          className="agent-workspace-header__close"
          type="text"
          icon={<CloseOutlined />}
          onClick={closeDrawer}
          aria-label="收拢 Agent"
        />
      </div>

      <div className="agent-workspace-body">
        <Card className="agent-workspace-panel" styles={{ body: { padding: 14 } }}>
          <div className="agent-workspace-results">
            {activeResult && readinessMeta ? (
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <div className="agent-overview-inline">
                <div className="agent-overview-chip agent-overview-chip--metric">
                  <div className="agent-overview-chip__value">{activeResult.field_patches.length}</div>
                  <div className="agent-overview-chip__label">结构化字段</div>
                </div>
                <div className="agent-overview-chip agent-overview-chip--metric">
                  <div className="agent-overview-chip__value">{activeResult.missing_required.length}</div>
                  <div className="agent-overview-chip__label">必填</div>
                </div>
                <div className="agent-overview-chip agent-overview-chip--metric">
                  <div className="agent-overview-chip__value">{conditionalRequirementItems.length}</div>
                  <div className="agent-overview-chip__label">模型相关</div>
                </div>
                <div className="agent-overview-chip agent-overview-chip--metric">
                  <div className="agent-overview-chip__value">{activeResult.conflicts.length}</div>
                  <div className="agent-overview-chip__label">冲突项</div>
                </div>
              </div>

              {activeResult.population_group || activeResult.detected_drug_id ? (
                <div className="agent-result-meta">
                  {activeResult.population_group ? (
                    <Tag className="agent-pill" color="blue">
                      参考标签：{getPopulationGroupLabel(activeResult.population_group)}
                    </Tag>
                  ) : null}
                  {activeResult.detected_drug_id ? (
                    <Tag className="agent-pill" color="geekblue">
                      药物：{getDrugLabel(activeResult.detected_drug_id, activeResult.detected_drug_id)}
                    </Tag>
                  ) : null}
                </div>
              ) : null}

              {activeResult.intake_context_error ? (
                <Alert type="error" showIcon message={activeResult.intake_context_error} />
              ) : null}

              {activeResult.intake_context && (
                <Card size="small" title="模型预匹配与自动计算" className="agent-section-card">
                  {activeResult.intake_context.recommended_models?.length ? (
                    <List
                      size="small"
                      dataSource={activeResult.intake_context.recommended_models.slice(0, 3)}
                      renderItem={(model) => (
                        <List.Item>
                          <Space direction="vertical" size={2} style={{ width: '100%' }}>
                            <Space wrap>
                              <Text strong>{getCanonicalModelDisplayName({ modelId: model.model_id, modelName: model.model_name })}</Text>
                              <Tag color="blue">匹配度 {Number(model.score).toFixed(1)}</Tag>
                              {model.matched_sub_population ? <Tag>{model.matched_sub_population}</Tag> : null}
                            </Space>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {model.match_reasons?.slice(0, 2).join(' · ') || '已通过当前患者边界匹配'}
                            </Text>
                          </Space>
                        </List.Item>
                      )}
                    />
                  ) : (
                    <Text type="secondary">当前尚未形成可确认的候选模型，补齐阻断字段后会自动刷新。</Text>
                  )}
                  {activeResult.intake_context.derived_covariates?.length ? (
                    <div style={{ marginTop: 10 }}>
                      <Text type="secondary">已自动计算：</Text>
                      <Space wrap style={{ marginTop: 6 }}>
                        {activeResult.intake_context.derived_covariates.slice(0, 8).map((item) => (
                          <Tag key={item.key} color="green">
                            {item.label}: {typeof item.value === 'number' ? item.value.toLocaleString('zh-CN', { maximumFractionDigits: 4 }) : String(item.value)}
                          </Tag>
                        ))}
                      </Space>
                    </div>
                  ) : null}
                </Card>
              )}

              {activeResult.missing_required.length > 0 && (
                <Card size="small" title="必填" className="agent-section-card">
                  <Space wrap>
                    {activeResult.missing_required.map((item) => (
                      <Tag key={item.key} color="red" className="agent-pill">
                        {item.label}
                      </Tag>
                    ))}
                  </Space>
                </Card>
              )}

              {conditionalRequirementItems.length > 0 && (
                <Card size="small" title="模型相关" className="agent-section-card">
                  <Space wrap>
                    {conditionalRequirementItems.map((item) => (
                      <Tooltip key={item.key} title={item.reason || (item.required_model_ids || []).join('、')}>
                        <Tag color="gold" className="agent-pill">{item.label}</Tag>
                      </Tooltip>
                    ))}
                  </Space>
                </Card>
              )}

              {optionalRequirementItems.length > 0 && (
                <Card size="small" title="选填" className="agent-section-card">
                  <Space wrap>
                    {optionalRequirementItems.slice(0, 8).map((item) => (
                      <Tag key={item.key} className="agent-pill">{item.label}</Tag>
                    ))}
                    {optionalRequirementItems.length > 8 ? (
                      <Tooltip title={optionalRequirementItems.slice(8).map((item) => item.label).join('、')}>
                        <Tag className="agent-pill">+{optionalRequirementItems.length - 8}</Tag>
                      </Tooltip>
                    ) : null}
                  </Space>
                </Card>
              )}

              {activeResult.conflicts.length > 0 && (
                <Card size="small" title="冲突待确认" className="agent-section-card">
                  <List
                    size="small"
                    dataSource={activeResult.conflicts}
                    renderItem={(item) => (
                      <List.Item>
                        <Text>{formatFieldLabelWithKey(item.key, effectiveRegistry)} · {formatReason(item.reason)}</Text>
                      </List.Item>
                    )}
                  />
                </Card>
              )}

              {activeResult.follow_up_questions.length > 0 && (
                <Card size="small" title="建议补问" className="agent-section-card">
                  <List
                    size="small"
                    dataSource={activeResult.follow_up_questions}
                    renderItem={(item) => <List.Item>{formatFollowUpQuestion(item)}</List.Item>}
                  />
                </Card>
              )}

              {computeSectionVisible && (
                <Card
                  size="small"
                  title="模型与剂量建议"
                  className="agent-section-card"
                  extra={activeResult.compute_status ? (
                    <Tag color={(COMPUTE_META[activeResult.compute_status] || COMPUTE_META.not_requested).color} className="agent-pill">
                      {(COMPUTE_META[activeResult.compute_status] || COMPUTE_META.not_requested).label}
                    </Tag>
                  ) : null}
                >
                  <Space direction="vertical" size={10} style={{ width: '100%' }}>
                    {activeResult.matched_model ? (
                      <div className="agent-field-row">
                        <div className="agent-field-row__top">
                          <div className="agent-field-row__identity">
                            <span className="agent-field-row__label">已匹配模型</span>
                          </div>
                          <div className="agent-field-row__value">{getCanonicalModelDisplayName({
                            modelId: activeResult.matched_model.model_id,
                            modelName: activeResult.matched_model.model_name,
                          })}</div>
                        </div>
                        <div className="agent-field-row__sub">
                          <div className="agent-field-row__evidence">{activeResult.matched_model.model_id}</div>
                        </div>
                      </div>
                    ) : null}

                    {doseCandidates.length > 0 ? (
                      <div className="agent-field-row">
                        <div className="agent-field-row__top">
                          <div className="agent-field-row__identity">
                            <span className="agent-field-row__label">候选方案</span>
                          </div>
                          <div className="agent-field-row__value">{doseCandidates.length} 个</div>
                        </div>
                        <div className="agent-field-row__sub">
                          <div className="agent-field-row__evidence">展示用于临床决策的结构化候选方案</div>
                        </div>
                        <List
                          size="small"
                          dataSource={doseCandidates.slice(0, 4)}
                          renderItem={(item: any, index) => (
                            <List.Item>
                              <Text>
                                方案 {index + 1}：
                                {item.label
                                  || `${item.dose_mg_kg !== undefined ? formatCandidateDosePerKg(item.dose_mg_kg) : '-'}${item.interval_h ? ` q${formatNumeric(Number(item.interval_h))}h` : ''}`}
                              </Text>
                            </List.Item>
                          )}
                        />
                      </div>
                    ) : null}
                  </Space>
                </Card>
              )}

              {activeResult.field_patches.length > 0 && (
                <div className="agent-field-section">
                  <div className="agent-field-section__header">
                    <div>
                      <Text strong>结构化字段建议</Text>
                      <div className="agent-field-section__subtext">字段、数值、状态与证据会集中展示在这里</div>
                    </div>
                    <Tag className="agent-pill" color="default">
                      {activeResult.field_patches.length} 项
                    </Tag>
                  </div>

                  <div className="agent-field-list">
                    {activeResult.field_patches.map((patch) => {
                      const statusMeta = FIELD_STATUS_META[patch.field_status] || FIELD_STATUS_META.ai_suggested;
                      const confidenceMeta = CONFIDENCE_META[String(patch.confidence_label || '').toLowerCase()] || {
                        color: 'default',
                        label: patch.confidence_label || patch.confidence.toFixed(2),
                      };

                      return (
                        <div key={patch.key} className="agent-field-row">
                          <div className="agent-field-row__top">
                            <div className="agent-field-row__identity">
                              <span className="agent-field-row__label">{formatFieldLabel(patch.key, effectiveRegistry)}</span>
                              <Tag color={statusMeta.color} className="agent-pill">
                                {statusMeta.label}
                              </Tag>
                              <Tag color={confidenceMeta.color} className="agent-pill">
                                {confidenceMeta.label}
                              </Tag>
                            </div>
                            <div className="agent-field-row__value">{formatPatchValue(patch)}</div>
                          </div>

                          {(patch.evidence_text || patch.unit) ? (
                            <div className="agent-field-row__sub">
                              <div className="agent-field-row__evidence">
                                {patch.evidence_text || `单位：${patch.unit}`}
                              </div>
                              {patch.unit ? (
                                <div className="agent-field-row__meta">
                                  <Tag className="agent-pill" color="default">
                                    {patch.unit}
                                  </Tag>
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <Space wrap>
                <Button type="primary" icon={<CheckCircleOutlined />} onClick={handleApply}>
                  回填到表单
                </Button>
                {activeResult.readiness_status === 'match_ready' ? (
                  <Tag color="green" icon={<FormOutlined />}>
                    已满足匹配准备条件
                  </Tag>
                ) : null}
              </Space>
              </Space>
            ) : (
              <div className="agent-empty-state">
                <div className="agent-empty-state__card">
                  <div className="agent-empty-state__icon">
                    <RobotOutlined />
                  </div>
                  <div className="agent-empty-state__title">粘贴病历后，Agent 会辅助生成结构化字段建议</div>
                  <div className="agent-empty-state__text">
                    它会帮助你补录字段、整理证据并提示仍需确认的信息，但不会替代表单本身。
                  </div>
                  <div className="agent-empty-state__tips">
                    <span className="agent-empty-state__tip">药物名称</span>
                    <span className="agent-empty-state__tip">检验结果</span>
                    <span className="agent-empty-state__tip">年龄 / 体重</span>
                    <span className="agent-empty-state__tip">诊疗记录</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="agent-workspace-input">
            <TextArea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="粘贴病历、检验结果或会话记录后，Agent 会辅助抽取药物、关键字段和缺失项，并持续判断是否达到匹配就绪态。"
              autoSize={{ minRows: 3, maxRows: 5 }}
              disabled={loading}
            />
            {aiExtractionSnapshot?.readiness_status ? (
              <div className="agent-workspace-status-row">
                <Tag
                  className="agent-pill"
                  color={
                    aiExtractionSnapshot.readiness_status === 'match_ready'
                      ? 'green'
                      : aiExtractionSnapshot.readiness_status === 'review_needed'
                        ? 'orange'
                        : 'default'
                  }
                >
                  上次状态：{READINESS_META[aiExtractionSnapshot.readiness_status].title}
                </Tag>
              </div>
            ) : null}
            {streamStatus ? (
              <div className="agent-workspace-status-row">
                <Tag className="agent-pill" color="processing">
                  {streamStatus}
                </Tag>
              </div>
            ) : null}
            <div className="agent-workspace-actions">
              <div className="agent-workspace-shortcuts">
                {intakeTemplates.map((template) => (
                  <Tooltip key={template.title} title="点击填入模板，可继续编辑后运行 Agent">
                    <Button
                      size="small"
                      disabled={loading}
                      onClick={() => setInputText(template.content)}
                    >
                      {template.title}
                    </Button>
                  </Tooltip>
                ))}
              </div>
              <div className="agent-workspace-actions__main">
                <Button
                  className="agent-clear-action"
                  icon={<DeleteOutlined />}
                  onClick={handleClearWorkspace}
                  disabled={loading || !canClearWorkspace}
                >
                  清空
                </Button>
                <div className="agent-workspace-actions__right">
                  <VoiceInputButton
                    onTranscript={(text: string) => setInputText((prev) => (prev ? `${prev}\n${text}` : text))}
                  />
                  <Button
                    className="agent-primary-action"
                    type="primary"
                    icon={<SendOutlined />}
                    loading={loading}
                    onClick={() => runAssistantTurn(inputText)}
                  >
                    运行 Agent
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

const AgentDrawer: React.FC = () => {
  const { drawerOpen, closeDrawer, toggleDrawer } = useAgent();
  const { isMobileShell } = useResponsive();

  return (
    <>
      <style>{AGENT_STYLES}</style>
      {!drawerOpen && (
        <Tooltip title="Agent 顾问" placement="left">
          <div className="agent-workspace-fab">
            <Button
              type="primary"
              icon={<RobotOutlined style={{ fontSize: 22, color: '#FFFFFF' }} />}
              onClick={toggleDrawer}
            />
          </div>
        </Tooltip>
      )}

      {isMobileShell ? (
        <Drawer
          rootClassName="agent-drawer"
          placement="bottom"
          height="100%"
          open={drawerOpen}
          onClose={closeDrawer}
          closable={false}
          styles={{
            body: { padding: 0, height: '100%', overflow: 'hidden' },
            header: { padding: 0, borderBottom: 'none' },
          }}
          title={null}
          extra={<Button type="text" icon={<CloseOutlined />} onClick={closeDrawer} />}
        >
          <AgentWorkspace />
        </Drawer>
      ) : drawerOpen ? (
        <div className="agent-docked">
          <AgentWorkspace />
        </div>
      ) : null}
    </>
  );
};

export default AgentDrawer;
